function getVisibleText(element) {
  if (!element) return "";

  const clone = element.cloneNode(true);
  clone.querySelectorAll(".sr-only").forEach((node) => node.remove());
  return clone.textContent.trim();
}

function parseMoneyToCents(text) {
  if (!text) return null;

  const numeric = text.replace(/[^\d.,]/g, "");
  if (!numeric) return null;

  const lastComma = numeric.lastIndexOf(",");
  const lastDot = numeric.lastIndexOf(".");
  let normalized;

  if (lastComma > lastDot) {
    normalized = numeric.replace(/\./g, "").replace(",", ".");
  } else {
    normalized = numeric.replace(/,/g, "");
  }

  const value = Number.parseFloat(normalized);
  if (Number.isNaN(value)) return null;

  return Math.round(value * 100);
}

function formatMoney(cents, format) {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }

  const formatString = format || window.themeVariables?.settings?.moneyFormat;
  if (typeof formatString !== "string") {
    return "";
  }

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  function formatWithDelimiters(number, precision = 2, thousands = ",", decimal = ".") {
    if (Number.isNaN(number) || number == null) {
      return "0";
    }

    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split(".");
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const fraction = parts[1] ? decimal + parts[1] : "";

    return dollars + fraction;
  }

  const match = formatString.match(placeholderRegex);
  if (!match?.[1]) {
    return formatWithDelimiters(cents, 2);
  }

  let value;

  switch (match[1]) {
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ", "");
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ",");
      break;
    case "amount_with_period_and_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  return formatString.replace(placeholderRegex, value);
}

class ProductTotalSavings extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this._observer = null;
    this._priceList = null;
    this._amountElement = this.querySelector(".product-total-savings__amount");

    this._setupPriceObserver();
    this._update();

    const form = this._getObservedForm();
    if (form) {
      form.addEventListener(
        "product:rerender",
        () => {
          this._teardownPriceObserver();
          this._setupPriceObserver();
          this._update();
        },
        { signal: this._abortController.signal }
      );
    }
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._teardownPriceObserver();
  }

  _getObservedForm() {
    const formId = this.closest("product-rerender")?.getAttribute("observe-form");
    return formId ? document.getElementById(formId) : null;
  }

  _getPriceList() {
    const container = this.closest("product-rerender");
    return container?.querySelector("price-list") || null;
  }

  _setupPriceObserver() {
    this._priceList = this._getPriceList();
    if (!this._priceList) return;

    this._observer = new MutationObserver(() => this._update());
    this._observer.observe(this._priceList, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  _teardownPriceObserver() {
    this._observer?.disconnect();
    this._observer = null;
    this._priceList = null;
  }

  _update() {
    const priceList = this._priceList || this._getPriceList();
    if (!priceList || !this._amountElement) {
      this.hidden = true;
      return;
    }

    const saleCents = parseMoneyToCents(getVisibleText(priceList.querySelector("sale-price")));
    const compareCents = parseMoneyToCents(getVisibleText(priceList.querySelector("compare-at-price")));

    if (saleCents == null || compareCents == null || compareCents <= saleCents) {
      this.hidden = true;
      return;
    }

    const savingsCents = compareCents - saleCents;
    const formattedAmount = formatMoney(
      savingsCents,
      window.themeVariables?.settings?.currencyCodeEnabled
        ? window.themeVariables?.settings?.moneyWithCurrencyFormat
        : window.themeVariables?.settings?.moneyFormat
    );
    const template = this.dataset.amountTemplate || "__AMOUNT__ off";

    this._amountElement.textContent = template.replace("__AMOUNT__", formattedAmount);
    this.hidden = false;
  }
}

if (!window.customElements.get("product-total-savings")) {
  window.customElements.define("product-total-savings", ProductTotalSavings);
}
